import {
  Button,
  cn,
  ConfirmDialog,
  DataTable,
  Dialog,
  DialogContent,
  Icon,
  IconButton,
  ResponsivePopover,
  StatusBadge,
  toast,
  type IconName,
} from '@nexus/ui';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { formatBytes, formatDateTime, formatRelative } from '../i18n/format';
import { useModules } from '../modules/context';
import type { OfflineChange, OfflineUpload } from '../offline/engine';
import { useOfflineEngine } from '../offline/offline';
import { useSyncStatus, type SyncState } from '../sync/sync-status';

const STATE_ICON: Record<SyncState, IconName> = {
  synced: 'cloud-check',
  syncing: 'refresh-cw',
  offline: 'cloud-off',
  pending: 'cloud-upload',
  attention: 'triangle-alert',
};

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2.5">
      <dt className="text-fg-muted">{label}</dt>
      <dd className="text-right font-medium text-fg tabular">{children}</dd>
    </div>
  );
}

function When({ date }: { date: Date | null }) {
  const { t, i18n } = useTranslation();
  if (!date) return <>{t('shell.sync.never')}</>;
  return (
    <time dateTime={date.toISOString()} title={formatDateTime(date, i18n.language)}>
      {formatRelative(date, i18n.language, t('common.relative.justNow'))}
    </time>
  );
}

/** Name of an offline entity: its label, else the name of its module. */
function useEntityName(): (entity: string, label?: string | null) => string {
  const { t } = useTranslation();
  const modules = useModules();
  return (entity, label) => {
    if (label) return t(label);
    const owner = modules.find((m) => entity.startsWith(`${m.manifest.id}.`));
    if (owner) return t(owner.manifest.name);
    // Kernel data without a label of its own.
    return entity.startsWith('core.') ? t('shell.sync.kernel.other') : entity;
  };
}

/** How much the browser lets this app store and how much it uses, while the panel is open. */
function useStorageEstimate(open: boolean): { usage: number; quota: number } | null {
  const [estimate, setEstimate] = useState<{ usage: number; quota: number } | null>(null);
  useEffect(() => {
    if (!open || !('storage' in navigator) || typeof navigator.storage.estimate !== 'function') {
      return;
    }
    let cancelled = false;
    navigator.storage
      .estimate()
      .then(({ usage, quota }) => {
        if (!cancelled && usage !== undefined && quota) setEstimate({ usage, quota });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [open]);
  return estimate;
}

/**
 * Sync status in the top bar with the detail panel (docs/ui/design-system.md, Shell): connection,
 * changes waiting for the server, changes the person has to decide on, and the offline data
 * (ADR-0013).
 */
export function SyncPanel() {
  const { t, i18n } = useTranslation();
  const status = useSyncStatus();
  const [open, setOpen] = useState(false);
  // Calm states show only the icon; offline and changes to check also name the state.
  const warning = status.state === 'offline' || status.state === 'attention';
  const state = t(`shell.sync.states.${status.state}`, { count: status.waiting });
  const label = `${t('shell.sync.title')}: ${state}`;
  const name = useEntityName();
  const labels = new Map(status.entities.map((e) => [e.entity, e.label]));
  const [comparing, setComparing] = useState<OfflineChange | null>(null);
  const [clearing, setClearing] = useState(false);
  const estimate = useStorageEstimate(open);
  const engine = useOfflineEngine();
  // Changes and files that never reached the server go with the storage.
  const unsent = status.pending + status.problems.length + status.uploads.length;

  async function clear() {
    try {
      await engine?.clear();
    } catch {
      toast.error(t('shell.sync.clearFailed'));
      throw new Error('clear-failed');
    }
    toast.success(t('shell.sync.cleared'));
    // What the device holds without preparation, e.g. catalogs, comes back right away.
    if (status.online) void status.syncNow();
  }

  return (
    <>
      <ResponsivePopover
        open={open}
        onOpenChange={setOpen}
        title={t('shell.sync.title')}
        trigger={
          warning ? (
            <Button
              variant="ghost"
              icon={STATE_ICON[status.state]}
              aria-label={label}
              className="px-2 text-warning hover:text-warning"
            >
              {state}
            </Button>
          ) : (
            <IconButton
              icon={STATE_ICON[status.state]}
              label={label}
              spin={status.state === 'syncing'}
              count={status.state === 'pending' ? status.waiting : undefined}
            />
          )
        }
      >
        <div className="px-4 pb-4">
          <dl className="divide-y divide-line">
            <Row label={t('shell.sync.connection')}>
              <span
                className={cn(
                  'inline-flex items-center gap-1.5',
                  status.online ? 'text-success' : 'text-warning',
                )}
              >
                <Icon name={status.online ? 'wifi' : 'wifi-off'} className="size-3.5" />
                {status.online ? t('shell.sync.online') : t('shell.sync.offline')}
              </span>
            </Row>
            <Row label={t('shell.sync.lastSync')}>
              <When date={status.lastContact} />
            </Row>
          </dl>
          <section className="mt-3">
            <h3 className="text-xs font-medium text-fg-subtle">{t('shell.sync.pending')}</h3>
            <p className="mt-1 text-fg-muted">
              {status.pending
                ? t('shell.sync.pendingCount', { count: status.pending })
                : t('shell.sync.pendingNone')}
            </p>
          </section>
          {status.problems.length > 0 && (
            <section className="mt-3">
              <h3 className="text-xs font-medium text-fg-subtle">{t('shell.sync.rejected')}</h3>
              <ul className="mt-1 divide-y divide-line">
                {status.problems.map((change) => (
                  <ProblemRow
                    key={change.id}
                    change={change}
                    name={name(change.entity, labels.get(change.entity))}
                    onCompare={() => setComparing(change)}
                  />
                ))}
              </ul>
            </section>
          )}
          {status.uploads.length > 0 && (
            <section className="mt-3">
              <h3 className="text-xs font-medium text-fg-subtle">{t('shell.sync.uploads')}</h3>
              <ul className="mt-1 divide-y divide-line">
                {status.uploads.map((upload) => (
                  <UploadRow
                    key={upload.fileId}
                    upload={upload}
                    name={name(upload.entity, labels.get(upload.entity))}
                  />
                ))}
              </ul>
            </section>
          )}
          <section className="mt-3">
            <h3 className="text-xs font-medium text-fg-subtle">{t('shell.sync.offlineData')}</h3>
            {status.entities.length ? (
              <dl className="divide-y divide-line">
                {status.entities.map((entity) => (
                  <Row key={entity.entity} label={name(entity.entity, entity.label)}>
                    <span className="flex flex-col items-end">
                      <span>{t('shell.sync.recordCount', { count: entity.count })}</span>
                      <span className="text-xs font-normal text-fg-subtle">
                        <When date={entity.lastSync} />
                      </span>
                    </span>
                  </Row>
                ))}
              </dl>
            ) : (
              <p className="mt-1 text-fg-muted">{t('shell.sync.offlineDataNone')}</p>
            )}
          </section>
          {status.entities.length > 0 && (
            <section className="mt-3">
              <h3 className="text-xs font-medium text-fg-subtle">{t('shell.sync.device')}</h3>
              <dl className="divide-y divide-line">
                <Row label={t('shell.sync.files')}>
                  {status.storage.files
                    ? t('shell.sync.fileCount', {
                        count: status.storage.files,
                        size: formatBytes(status.storage.bytes, i18n.language),
                      })
                    : t('shell.sync.filesNone')}
                </Row>
                {status.storage.prepared > 0 && (
                  <Row label={t('shell.sync.prepared')}>
                    {t('shell.sync.recordCount', { count: status.storage.prepared })}
                  </Row>
                )}
                {estimate && (
                  <Row label={t('shell.sync.storage')}>
                    {t('shell.sync.storageUsed', {
                      used: formatBytes(estimate.usage, i18n.language),
                      quota: formatBytes(estimate.quota, i18n.language),
                    })}
                  </Row>
                )}
              </dl>
              {status.storage.full && (
                <p className="mt-1 flex items-start gap-1.5 text-warning">
                  <Icon name="triangle-alert" className="mt-0.5 size-3.5 shrink-0" />
                  {t('shell.sync.storageFull')}
                </p>
              )}
              <Button
                className="mt-2"
                variant="ghost"
                size="sm"
                icon="trash-2"
                disabled={!engine}
                onClick={() => {
                  setOpen(false);
                  setClearing(true);
                }}
              >
                {t('shell.sync.clear')}
              </Button>
            </section>
          )}
          <Button
            className="mt-4"
            block
            icon="refresh-cw"
            loading={status.state === 'syncing'}
            disabled={!status.online}
            onClick={() => void status.syncNow()}
          >
            {t('shell.sync.syncNow')}
          </Button>
        </div>
      </ResponsivePopover>
      <ConflictDialog change={comparing} onClose={() => setComparing(null)} />
      <ConfirmDialog
        open={clearing}
        onOpenChange={setClearing}
        tone="danger"
        title={t('shell.sync.clearTitle')}
        description={t('shell.sync.clearDescription')}
        confirmLabel={t('shell.sync.clearConfirm')}
        onConfirm={clear}
        confirmText={
          unsent
            ? { value: t('shell.sync.clearWord'), label: t('shell.sync.clearWordLabel') }
            : undefined
        }
      >
        {unsent > 0 && (
          <p className="flex items-start gap-1.5 text-danger">
            <Icon name="triangle-alert" className="mt-0.5 size-3.5 shrink-0" />
            {t('shell.sync.clearUnsent', { count: unsent })}
          </p>
        )}
      </ConfirmDialog>
    </>
  );
}

function ProblemRow({
  change,
  name,
  onCompare,
}: {
  change: OfflineChange;
  name: string;
  onCompare: () => void;
}) {
  const { t, i18n } = useTranslation();
  const engine = useOfflineEngine();
  const reason = change.reason ?? '';
  // Codes of the module first (manifest `offline[].reasons`), then those of the kernel.
  const reasonKey = [
    `${change.entity.split('.')[0]}.sync.reasons.${reason}`,
    `shell.sync.reasons.${reason}`,
  ].find((key) => i18n.exists(key));
  return (
    <li className="flex items-center justify-between gap-3 py-2">
      <span className="flex min-w-0 flex-col">
        <span className="truncate font-medium text-fg">{name}</span>
        <span className="text-xs text-fg-muted">
          {change.state === 'conflict'
            ? t('shell.sync.conflict')
            : t(reasonKey ?? 'shell.sync.reasons.other')}
        </span>
      </span>
      {change.state === 'conflict' ? (
        <Button size="sm" icon="git-compare" onClick={onCompare}>
          {t('shell.sync.compare')}
        </Button>
      ) : (
        <Button size="sm" variant="ghost" onClick={() => void engine?.discard(change.id)}>
          {t('shell.sync.discard')}
        </Button>
      )}
    </li>
  );
}

/** A file waiting for its upload, or refused with the reason and what the person can do. */
function UploadRow({ upload, name }: { upload: OfflineUpload; name: string }) {
  const { t, i18n } = useTranslation();
  const engine = useOfflineEngine();
  const reasonKey = `shell.sync.reasons.${upload.error ?? ''}`;
  const reason = upload.error
    ? i18n.exists(reasonKey)
      ? t(reasonKey)
      : t('shell.sync.reasons.other')
    : null;
  const state =
    upload.state === 'uploading'
      ? t('shell.sync.uploading')
      : upload.state === 'failed'
        ? (reason ?? t('shell.sync.reasons.other'))
        : reason
          ? t('shell.sync.uploadWaitingBecause', { reason })
          : t('shell.sync.uploadWaiting');
  return (
    <li className="flex items-center justify-between gap-3 py-2">
      <span className="flex min-w-0 flex-col">
        <span className="truncate font-medium text-fg">{upload.name || t('files.expired')}</span>
        <span
          className={cn('text-xs', upload.state === 'failed' ? 'text-danger' : 'text-fg-muted')}
        >
          {`${name} · ${state}`}
        </span>
      </span>
      {upload.state === 'failed' && (
        <span className="flex shrink-0 gap-1">
          {upload.name && (
            <Button
              size="sm"
              icon="rotate-ccw"
              onClick={() => {
                void engine?.retryUpload(upload.fileId).then(() => {
                  if (navigator.onLine) void engine.sync().catch(() => undefined);
                });
              }}
            >
              {t('shell.sync.retryUpload')}
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => void engine?.discardUpload(upload.fileId)}
          >
            {t('shell.sync.discard')}
          </Button>
        </span>
      )}
    </li>
  );
}

function show(value: unknown): string {
  if (value === undefined || value === null || value === '') return '–';
  return typeof value === 'string' ? value : JSON.stringify(value);
}

/** Both states of a conflicting change side by side; the person picks one (ADR-0013). */
function ConflictDialog({
  change,
  onClose,
}: {
  change: OfflineChange | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const engine = useOfflineEngine();
  const current = change?.current;
  const gone = !current || current.deleted;
  const fields = change ? Object.keys(change.changes) : [];

  async function decide(keepMine: boolean) {
    if (!change || !engine) return;
    if (keepMine) await engine.retry(change.id);
    else await engine.discard(change.id);
    toast.success(t(keepMine ? 'shell.sync.keptMine' : 'shell.sync.keptServer'));
    onClose();
    if (keepMine && navigator.onLine) void engine.sync().catch(() => undefined);
  }

  return (
    <Dialog open={!!change} onOpenChange={(open) => !open && onClose()}>
      {change && (
        <DialogContent
          title={t('shell.sync.compareTitle')}
          description={gone ? t('shell.sync.deletedOnServer') : t('shell.sync.compareDescription')}
          size="lg"
          footer={
            <>
              <Button onClick={() => void decide(false)}>{t('shell.sync.keepServer')}</Button>
              {!gone && (
                <Button variant="primary" onClick={() => void decide(true)}>
                  {t('shell.sync.keepMine')}
                </Button>
              )}
            </>
          }
        >
          {!gone && fields.length > 0 && (
            <DataTable
              caption={t('shell.sync.compareTitle')}
              rows={fields}
              rowKey={(field) => field}
              columns={[
                {
                  id: 'field',
                  header: t('shell.sync.field'),
                  width: 'md',
                  cell: (field) => <span className="font-mono text-xs text-fg-muted">{field}</span>,
                },
                {
                  id: 'mine',
                  header: t('shell.sync.mine'),
                  cell: (field) => show(change.changes[field]),
                },
                {
                  id: 'server',
                  header: t('shell.sync.server'),
                  cell: (field) => (
                    <span className="inline-flex flex-wrap items-center gap-2">
                      {show(current.data[field])}
                      {show(current.data[field]) !== show(change.changes[field]) && (
                        <StatusBadge status="warning">{t('shell.sync.differs')}</StatusBadge>
                      )}
                    </span>
                  ),
                },
              ]}
              mobile={{ title: 'field', fields: ['mine', 'server'] }}
            />
          )}
        </DialogContent>
      )}
    </Dialog>
  );
}
